import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import ProfilePicture from '@components/molecules/profile/profile-picture';
import Icon from '@components/atoms/icon/icon';
import Button from '@components/atoms/button/button';
import Spinner from '@components/atoms/spinner/spinner';
import useUser from '@hooks/user/use-user';
import useUploadProfilePicture from '@hooks/user/use-upload-profile-picture';
import useDeleteProfilePicture from '@hooks/user/use-delete-profile-picture';
import styles from './account-picture.module.scss';

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png';

const AccountPicture: React.FC = () => {
  const { t } = useTranslation('account-settings');
  const { user } = useUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadProfilePicture, isLoading: isUploading } = useUploadProfilePicture();
  const { mutate: deleteProfilePicture, isLoading: isDeleting } = useDeleteProfilePicture();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const handleUploadClick = () => {
    setMenuOpen(false);
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadProfilePicture(file);
    }
    event.target.value = '';
  };

  const handleDeleteClick = () => {
    setMenuOpen(false);
    deleteProfilePicture();
  };

  if (!user) {
    return null;
  }

  return (
    <div
      className={styles.accountPicture}
      ref={containerRef}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        onChange={handleFileChange}
        hidden
        data-cy="account-picture-file-input"
      />
      <button
        className={styles.accountPicture__trigger}
        onClick={() => setMenuOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={menuOpen}
        aria-label={t('ACCOUNT_SETTINGS.EDIT')}
        disabled={isUploading || isDeleting}
        data-cy="account-picture-trigger"
      >
        <ProfilePicture
          picturePath={user.picture?.path}
          userId={String(user.id)}
          isUserProfile
          disableLazyLoad
          width={120}
          height={120}
          wrapperProps={{ className: styles.accountPicture__imageWrapper }}
        />
        {isUploading || isDeleting ? (
          <span
            className={`${styles.accountPicture__overlay} ${styles['accountPicture__overlay--loading']}`}
            aria-hidden="true"
          >
            <Spinner color="white" />
          </span>
        ) : (
          <span
            className={styles.accountPicture__overlay}
            aria-hidden="true"
          >
            <Icon
              name="edit-default"
              ariaHidden
              className={styles.accountPicture__overlay__icon}
            />
            <span className={styles.accountPicture__overlay__label}>{t('ACCOUNT_SETTINGS.EDIT')}</span>
          </span>
        )}
      </button>
      {menuOpen && (
        <div className={styles.accountPicture__menu}>
          <Button
            disableDefaultStyling
            className={styles.accountPicture__menu__item}
            onClick={handleUploadClick}
            data-cy="account-picture-upload"
          >
            <Icon
              name="photo-library-default"
              ariaHidden
              className={styles.accountPicture__menu__item__icon}
            />
            <span>{t('ACCOUNT_SETTINGS.PROFILE_PICTURE.UPLOAD')}</span>
          </Button>
          <Button
            disableDefaultStyling
            className={styles.accountPicture__menu__item}
            onClick={handleDeleteClick}
            data-cy="account-picture-delete"
          >
            <Icon
              name="delete-default"
              ariaHidden
              className={styles.accountPicture__menu__item__icon}
            />
            <span>{t('ACCOUNT_SETTINGS.PROFILE_PICTURE.DELETE')}</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default AccountPicture;
